import { Injectable, NgZone, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalCustomNavigationClient, MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { Observable, ReplaySubject, Subject, filter, firstValueFrom, map, race, take, takeUntil, timer } from 'rxjs';
import { MICROSOFT_SSO_CONFIG, isDebugMode } from '../../config/sso.config';
import { LoggerService } from './logger.service';
import { MsalPublicClientApplication } from '../models/window-extensions';

/**
 * Microsoft Authentication Service
 *
 * Dedicated service for Microsoft Azure AD authentication using MSAL (Microsoft Authentication Library).
 * This service handles all Microsoft-specific authentication operations including login, logout,
 * token management, and user profile access.
 *
 * Key Features:
 * - Microsoft Azure AD integration via MSAL
 * - Popup and redirect authentication flows
 * - Automatic token refresh and management
 * - User profile and account information access
 * - Reactive authentication state management
 * - Comprehensive error handling and logging
 *
 * Prerequisites:
 * 1. Azure AD App Registration configured in Azure Portal
 * 2. Valid Client ID and Tenant ID in sso.config.ts
 * 3. Redirect URIs properly configured in Azure AD
 * 4. MSAL providers configured in app.config.ts
 *
 * @injectable
 * @providedIn 'root'
 */
@Injectable({
  providedIn: 'root',
})
export class MicrosoftAuthService {
  private readonly ngZone = inject(NgZone);
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  private readonly destroy$ = new Subject<void>();
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _userProfile = signal<AccountInfo | null>(null);
  // ReplaySubject(1): the redirect result fires ONCE at app boot (the
  // handleRedirectObservable resolution), but consumers subscribe LATER —
  // the root guard navigates to the account-setup resume route only after
  // auth state settles, and that component must still see the result. A
  // plain Subject drops the emission for late subscribers (the SSO-link
  // resume race); replaying the last emission fixes it. AuthService's boot
  // exchange subscribes at construction, so it is unaffected either way.
  private readonly _redirectResult = new ReplaySubject<AuthenticationResult | null>(1);
  private _isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  /** Observable stream of Microsoft authentication state changes */
  readonly isAuthenticated$ = toObservable(this._isAuthenticated);

  /** Observable stream of Microsoft user profile changes */
  readonly userProfile$ = toObservable(this._userProfile);

  /**
   * Observable stream of completed MSAL redirect results (task 4.3, Defect 1).
   *
   * Emits the `AuthenticationResult` whenever `handleRedirectObservable()`
   * resolves after a login redirect — including `result.idToken`, which the
   * backend exchange needs. AuthService subscribes to this (NOT the other way
   * around — MicrosoftAuthService must not inject AuthService, which would be
   * a circular dependency) and POSTs the ID token to
   * `/parent-auth/sso/microsoft` to establish the GTLC session.
   *
   * Emits `null` when the redirect resolved without a result (e.g. a page
   * load that was not a redirect round-trip), so consumers can distinguish
   * "nothing happened" from "token acquired".
   *
   * REPLAY: the last emission is replayed to late subscribers — the
   * account-setup SSO-link resume subscribes after the root guard navigates,
   * which is strictly after this resolved at boot.
   */
  readonly redirectResult$ = this._redirectResult.asObservable();

  /** Default scopes requested during Microsoft authentication */
  private readonly defaultScopes = MICROSOFT_SSO_CONFIG.scopes;

  constructor() {
    // Don't automatically initialize - wait for first method call
    // This prevents MSAL from being accessed before it's ready during app startup
    if (isDebugMode()) {
      this.logger.debug('Microsoft Auth Service created - initialization deferred');
    }
  }

  /**
   * Ensure that MSAL is initialized
   *
   * @private
   */
  private async ensureInitialized(): Promise<void> {
    if (this._isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.initializeMsal();
    await this.initializationPromise;
  }

  /**
   * Initialize the Microsoft authentication service
   *
   * Public method to ensure MSAL is initialized and ready for use.
   * This should be called before using any authentication methods.
   *
   * @returns Promise that resolves when initialization is complete
   */
  async initialize(): Promise<void> {
    return this.ensureInitialized();
  }

  /**
   * Initialize MSAL instance and authentication system
   *
   * @private
   */
  private async initializeMsal(): Promise<void> {
    try {
      // Wire the Angular-router navigation client BEFORE initialize():
      // MSAL's redirect handler navigates the browser back to the page that
      // started the login (request.origin) AFTER processing the returned
      // hash — a FULL page unload that aborts every in-flight request and
      // destroys the JS context before the redirect result can be consumed
      // (observed live 2026-09-11: the exchange POST and whoami were both
      // killed mid-flight; the second boot had no hash and no result, so
      // the login silently did nothing). MsalCustomNavigationClient routes
      // that navigation through the Angular Router instead — SPA-internal,
      // no unload, the result emission and the exchange complete normally.
      this.msalService.instance.setNavigationClient(
        new MsalCustomNavigationClient(this.msalService, this.router, this.location),
      );

      // Initialize the MSAL instance
      await this.msalService.instance.initialize();

      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: MSAL instance initialized successfully');
      }

      // Set up authentication monitoring
      this.initializeAuthentication();

      this._isInitialized = true;

      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth Service initialized with config:', MICROSOFT_SSO_CONFIG);
      }
    } catch (error) {
      this.logger.error('Microsoft Auth: Failed to initialize MSAL:', error);
      throw error;
    }
  }

  /**
   * Initialize Microsoft authentication system
   *
   * Sets up MSAL interaction monitoring and automatically updates authentication
   * state when MSAL operations complete. This ensures the service stays in sync
   * with MSAL's internal state changes.
   *
   * @private
   */
  private initializeAuthentication(): void {
    // Handle redirect response first
    //
    // navigateToLoginRequestUrl: false (observed live 2026-09-15): msal-browser
    // v5's handleRedirectPromise reads this ONLY from this per-call options
    // object — it does NOT fall back to the `PublicClientApplication` config's
    // `auth.navigateToLoginRequestUrl` (confirmed by reading msal-browser's
    // source; that config field is a no-op for this call). Without it here,
    // MSAL defaults to true: it caches the page the redirect was INITIATED
    // from (redirectStartPage — the /login page, since that's where the
    // button lives) and, on landing back with the auth hash, does a HARD
    // `window.location.replace(loginRequestUrl)` to return there via
    // `MsalCustomNavigationClient`'s `noHistory` branch BEFORE processing the
    // response. That extra full-page reload (confirmed via a Navigation
    // Timing `navigationType: 'navigate'` entry, not a route change) is what
    // rendered the real /login form for an instant on every SSO login,
    // regardless of the root-guard/exchangeInProgress timing fix above (the
    // hash is gone by the time that reload's guard runs, so it never even
    // sees a redirect in flight). Passing it explicitly false keeps MSAL on
    // the redirect_uri page (bare origin) and skips that reload entirely.
    this.msalService
      .handleRedirectObservable({ navigateToLoginRequestUrl: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result) {
            if (isDebugMode()) {
              this.logger.debug('Microsoft Auth: Redirect response handled:', result);
            }
            this.checkAndSetActiveAccount();
            this.updateAuthenticationState();
            // Publish the redirect result (task 4.3, Defect 1): the ID token
            // must reach AuthService for the backend exchange — discarding it
            // here left the parent unauthenticated after a successful
            // Microsoft redirect.
            this._redirectResult.next(result);
          } else {
            // Not a redirect round-trip (plain page load) — publish null so
            // consumers know no token is pending.
            this._redirectResult.next(null);
          }
        },
        error: (error) => {
          this.logger.error('Microsoft Auth: Error handling redirect response:', error);
          // A failed redirect round-trip is a FAILED login: surface it so the
          // UI shows an error instead of silently bouncing back unauthenticated.
          this._redirectResult.error(error);
        },
      });

    // Set up authentication state monitoring after MSAL is ready
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Only check/update after MSAL is initialized
        if (this._isInitialized) {
          this.checkAndSetActiveAccount();
          this.updateAuthenticationState();
        }
      });

    // Initial state update now that MSAL is ready
    this.checkAndSetActiveAccount();
    this.updateAuthenticationState();

    if (isDebugMode()) {
      this.logger.debug('Microsoft Auth: Authentication monitoring initialized');
    }
  }

  /**
   * Check and set the active account in MSAL
   *
   * Ensures that MSAL has an active account set when there are available accounts.
   * This is important for proper token acquisition and user identification.
   * MSAL requires an explicit active account to be set for silent token acquisition.
   *
   * @private
   */
  private checkAndSetActiveAccount(): void {
    // Only check accounts if MSAL is initialized
    if (!this._isInitialized) {
      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: Skipping active account check - MSAL not initialized');
      }
      return;
    }

    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);

      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: Set active account:', accounts[0]);
      }
    }
  }

  /**
   * Clear any ongoing interaction state in MSAL
   * This helps prevent 'interaction_in_progress' errors
   *
   * @private
   */
  private clearInteractionState(): void {
    try {
      // Force clear any interaction in progress state
      const msalInstance = this.msalService.instance as unknown as MsalPublicClientApplication;
      if (msalInstance.browserStorage) {
        msalInstance.browserStorage.removeItem('interaction_in_progress');
        msalInstance.browserStorage.removeItem('interaction_type');
      }
      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: Cleared interaction state');
      }
    } catch (error) {
      if (isDebugMode()) {
        this.logger.warn('Microsoft Auth: Could not clear interaction state:', error);
      }
    }
  }

  /**
   * Clear a STUCK MSAL interaction state (public recovery path)
   *
   * For the account-setup SSO-link flow: a previous attempt's interaction
   * can become unfinishable (its cached request references an auth code
   * that was already consumed by an earlier page load). MSAL then blocks
   * every new interactive flow with interaction_in_progress, and no storage
   * clear fixes the in-memory state. The caller clears the cached
   * interaction keys and RELOADS the page — a fresh MSAL instance starts
   * clean. The staged SSO link (SsoLinkBridge, sessionStorage) survives the
   * reload and the retry goes straight to the resume path.
   *
   * The pending-request keys are removed too: they reference the consumed
   * code and would only re-trigger the stuck processing after the reload.
   */
  clearStuckInteractionState(): void {
    try {
      this.clearInteractionState();
      const msalInstance = this.msalService.instance as unknown as MsalPublicClientApplication;
      if (msalInstance.browserStorage) {
        // MSAL v5 caches the pending redirect request under
        // '{clientId}.request.params' / '{clientId}.request.origin' —
        // remove every cache entry that references a pending interaction.
        const storage = msalInstance.browserStorage;
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.sessionStorage.length; i++) {
          const key = window.sessionStorage.key(i);
          if (key && (key.includes('.request.params') || key.includes('.request.origin') || key.includes('interaction'))) {
            keysToRemove.push(key);
          }
        }
        for (const key of keysToRemove) {
          storage.removeItem(key);
          window.sessionStorage.removeItem(key);
        }
      }
      this.logger.warn('Microsoft Auth: Cleared stuck interaction state — reload required');
    } catch (error) {
      this.logger.warn('Microsoft Auth: Could not clear stuck interaction state:', error);
    }
  }

  /**
   * Update the service's authentication state based on MSAL's active account
   *
   * Synchronizes the service's reactive state observables with MSAL's current state.
   * This method should be called whenever MSAL's state potentially changes
   * (after login, logout, token refresh, etc.).
   *
   * @private
   */
  private updateAuthenticationState(): void {
    // Only update state if MSAL is initialized
    if (!this._isInitialized) {
      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: Skipping state update - MSAL not initialized');
      }
      return;
    }

    const activeAccount = this.msalService.instance.getActiveAccount();
    const isAuthenticated = !!activeAccount;

    this.ngZone.run(() => {
      this._isAuthenticated.set(isAuthenticated);
      this._userProfile.set(activeAccount);

      if (isDebugMode()) {
        this.logger.debug(
          'Microsoft Auth: State updated - authenticated:',
          isAuthenticated,
          'account:',
          activeAccount
        );
      }
    });
  }

  /**
   * Initiate Microsoft Azure AD login using popup window
   *
   * Opens a popup window for Azure AD authentication. This method provides a
   * smoother user experience as it doesn't redirect away from the current page.
   * However, popup blockers may interfere with this method.
   *
   * @param userFlowRequest - Optional additional parameters for the login request
   * @returns Observable that emits the authentication result when login completes
   *
   * @example
   * ```typescript
   * try {
   *   const result = await firstValueFrom(this.microsoftAuthService.signInWithPopup());
   *   console.log('Microsoft login successful:', result);
   * } catch (error) {
   *   console.error('Microsoft login failed:', error);
   * }
   * ```
   */
  signInWithPopup(userFlowRequest?: PopupRequest): Observable<AuthenticationResult> {
    return new Observable<AuthenticationResult>((subscriber) => {
      this.ensureInitialized()
        .then(() => {
          // Clear any existing interaction state before attempting login
          this.clearInteractionState();

          const request: PopupRequest = {
            scopes: this.defaultScopes,
            ...userFlowRequest,
          };

          if (isDebugMode()) {
            this.logger.debug('Microsoft Auth: Starting popup login with request:', request);
          }

          this.msalService.loginPopup(request).subscribe({
            next: (result: AuthenticationResult) => {
              this.msalService.instance.setActiveAccount(result.account);
              this.updateAuthenticationState();

              if (isDebugMode()) {
                this.logger.debug('Microsoft Auth: Popup login successful:', result);
              }

              subscriber.next(result);
              subscriber.complete();
            },
            error: (error) => {
              this.logger.error('Microsoft Auth: Popup login failed:', error);
              subscriber.error(error);
            },
          });
        })
        .catch((error) => {
          this.logger.error('Microsoft Auth: Initialization failed before popup login:', error);
          subscriber.error(error);
        });
    });
  }

  /**
   * Wait for MSAL's interaction state to settle (inProgress → None)
   *
   * loginRedirect() throws interaction_in_progress while ANY interaction is
   * in progress — including the redirect processing MSAL runs at boot for a
   * pending interaction from a previous attempt (observed live 2026-09-11:
   * initialize() resolves while handleRedirectObservable is still exchanging
   * the cached code). Callers must await this before starting a new
   * interactive flow.
   *
   * @param timeoutMs - Maximum time to wait before giving up
   * @returns true when the interaction settled to None; false on timeout
   *          (the interaction is stuck — the caller should recover, e.g.
   *          clear MSAL's cached interaction state and reload)
   */
  waitForInteractionSettle(timeoutMs = 5000): Promise<boolean> {
    return firstValueFrom(
      race(
        this.msalBroadcastService.inProgress$.pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          take(1),
          map(() => true),
        ),
        timer(timeoutMs).pipe(map(() => false)),
      ),
    );
  }

  /**
   * Initiate Microsoft Azure AD login using redirect flow
   *
   * Redirects the entire browser window to Azure AD for authentication.
   * After successful authentication, the user is redirected back to the
   * application. This method is more reliable than popup but provides
   * a less seamless user experience.
   *
   * @param userFlowRequest - Optional additional parameters for the login request
   *
   * @example
   * ```typescript
   * this.microsoftAuthService.signInWithRedirect();
   * // User will be redirected to Microsoft login page
   * ```
   */
  signInWithRedirect(userFlowRequest?: RedirectRequest): void {
    this.ensureInitialized()
      .then(async () => {
        // Wait for MSAL to finish processing any PENDING interaction from a
        // previous attempt (the boot's handleRedirectObservable runs
        // asynchronously after ensureInitialized resolves). loginRedirect
        // throws interaction_in_progress while inProgress != None — the
        // account-setup flow hit this (Follow-up #6) and the login page hit
        // it too (user report: "only after multiple attempts"). The 5s
        // timeout bounds the wait; clearInteractionState below recovers the
        // storage side of a genuinely stuck interaction.
        await this.waitForInteractionSettle(5000);

        // Clear any existing interaction state before attempting login
        this.clearInteractionState();

        const request: RedirectRequest = {
          scopes: this.defaultScopes,
          ...userFlowRequest,
        };

        if (isDebugMode()) {
          this.logger.debug('Microsoft Auth: Starting redirect login with request:', request);
        }

        this.msalService.loginRedirect(request);
      })
      .catch((error) => {
        this.logger.error('Microsoft Auth: Initialization failed before redirect login:', error);
      });
  }

  /**
   * Sign out the current Microsoft user using popup
   *
   * Opens a popup window to complete the Azure AD sign-out process.
   * This clears all authentication tokens and signs the user out of Azure AD.
   *
   * @returns Observable that completes when logout is finished
   *
   * @example
   * ```typescript
   * try {
   *   await firstValueFrom(this.microsoftAuthService.signOut());
   *   console.log('Microsoft logout successful');
   * } catch (error) {
   *   console.error('Microsoft logout failed:', error);
   * }
   * ```
   */
  signOut(): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.ensureInitialized()
        .then(async () => {
          const account = this.msalService.instance.getActiveAccount();

          if (!account) {
            if (isDebugMode()) {
              this.logger.debug('Microsoft Auth: No active account to logout');
            }
            // Ensure local state is cleared even if no active account
            await this.clearAllAuthenticationData();
            subscriber.next();
            subscriber.complete();
            return;
          }

          if (isDebugMode()) {
            this.logger.debug('Microsoft Auth: Starting redirect logout for account:', account);
          }

          // IMPORTANT: Clear local authentication state IMMEDIATELY before redirect
          // This prevents issues if user hits back button during logout redirect
          await this.clearAllAuthenticationData();

          // Use redirect logout instead of popup to avoid browser policy issues
          try {
            this.msalService.logoutRedirect({
              account: account,
              postLogoutRedirectUri: MICROSOFT_SSO_CONFIG.postLogoutRedirectUri,
            });

            // For redirect logout, we don't wait for completion since it will redirect the page
            if (isDebugMode()) {
              this.logger.debug(
                'Microsoft Auth: Redirect logout initiated successfully, local state cleared'
              );
            }

            subscriber.next();
            subscriber.complete();
          } catch (error) {
            if (isDebugMode()) {
              this.logger.error('Microsoft Auth: Redirect logout failed:', error);
            }
            // If logout fails, we should still clear local state to be safe
            await this.clearAllAuthenticationData();
            subscriber.error(error);
          }
        })
        .catch(async (error) => {
          this.logger.error('Microsoft Auth: Initialization failed before logout:', error);
          // Clear state even if initialization failed
          await this.clearAllAuthenticationData();
          subscriber.error(error);
        });
    });
  }

  /**
   * Check if the error indicates user cancelled the logout
   */
  private isUserCancellationError(error: unknown): boolean {
    if (!error) return false;

    // MSAL-specific error codes for user cancellation
    const cancellationErrorCodes = [
      'user_cancelled',
      'UserCancelledPopupError',
      'BrowserAuthError: popup_closed_by_user',
      'popup_closed_by_user',
      'popup_window_error',
      'user_cancelled_error',
      'interaction_in_progress',
    ];

    const err = error as { message?: string; errorCode?: string; code?: string; name?: string };
    const errorMessage = err.message || err.errorCode || err.code || err.name || '';
    const errorString = errorMessage.toString().toLowerCase();

    return cancellationErrorCodes.some((code) => errorString.includes(code.toLowerCase()));
  }

  /**
   * Sign out using redirect flow
   *
   * Redirects the entire browser window to Azure AD sign-out page.
   * After sign-out, the user is redirected back to the specified URL.
   *
   * @param postLogoutRedirectUri - Optional URL to redirect to after logout
   *
   * @example
   * ```typescript
   * this.microsoftAuthService.signOutWithRedirect('/login');
   * ```
   */
  signOutWithRedirect(postLogoutRedirectUri?: string): void {
    this.ensureInitialized()
      .then(() => {
        const account = this.msalService.instance.getActiveAccount();

        if (!account) {
          if (isDebugMode()) {
            this.logger.debug('Microsoft Auth: No active account to logout');
          }
          return;
        }

        if (isDebugMode()) {
          this.logger.debug('Microsoft Auth: Starting redirect logout for account:', account);
        }

        // Clear local authentication state before redirecting
        this._isAuthenticated.set(false);
        this._userProfile.set(null);

        // Clear interaction state before logout
        this.clearInteractionState();

        this.msalService.logoutRedirect({
          account: account,
          postLogoutRedirectUri:
            postLogoutRedirectUri || MICROSOFT_SSO_CONFIG.postLogoutRedirectUri,
        });
      })
      .catch((error) => {
        this.logger.error('Microsoft Auth: Initialization failed before redirect logout:', error);
      });
  }

  /**
   * Acquire access token silently
   *
   * Attempts to acquire an access token without user interaction.
   * This method tries to use cached tokens first, then attempts silent token refresh.
   * Falls back to interactive authentication if silent acquisition fails.
   *
   * @param request - Optional token acquisition parameters
   * @returns Observable that emits the authentication result
   *
   * @example
   * ```typescript
   * try {
   *   const result = await firstValueFrom(this.microsoftAuthService.acquireTokenSilently());
   *   const token = result.accessToken;
   * } catch (error) {
   *   // Handle token acquisition failure
   * }
   * ```
   */
  acquireTokenSilently(request?: Partial<SilentRequest>): Observable<AuthenticationResult> {
    return new Observable<AuthenticationResult>((subscriber) => {
      this.ensureInitialized()
        .then(() => {
          const silentRequest: SilentRequest = {
            account: this.msalService.instance.getActiveAccount()!,
            scopes: this.defaultScopes,
            ...request,
          };

          if (isDebugMode()) {
            this.logger.debug('Microsoft Auth: Acquiring token silently with request:', silentRequest);
          }

          this.msalService.acquireTokenSilent(silentRequest).subscribe({
            next: (result: AuthenticationResult) => {
              if (isDebugMode()) {
                this.logger.debug('Microsoft Auth: Token acquired silently:', result);
              }
              subscriber.next(result);
              subscriber.complete();
            },
            error: (error) => {
              this.logger.error('Microsoft Auth: Silent token acquisition failed:', error);
              subscriber.error(error);
            },
          });
        })
        .catch((error) => {
          this.logger.error('Microsoft Auth: Initialization failed before token acquisition:', error);
          subscriber.error(error);
        });
    });
  }

  /**
   * Get access token as string
   *
   * Convenience method that acquires a token and returns just the token string.
   *
   * @param scopes - Optional array of scopes to request
   * @returns Observable that emits the access token string
   */
  getAccessToken(scopes: string[] = []): Observable<string> {
    const request: Partial<SilentRequest> = scopes.length > 0 ? { scopes } : {};

    return this.acquireTokenSilently(request).pipe(
      map((result: AuthenticationResult) => result.accessToken)
    );
  }

  /**
   * Get the currently active Microsoft user account
   *
   * Returns the account information for the currently signed-in Microsoft user.
   *
   * @returns The active account information or null if no user is signed in
   */
  getCurrentUser(): AccountInfo | null {
    if (!this._isInitialized) {
      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: Cannot get current user - MSAL not initialized');
      }
      return null;
    }
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Get all Microsoft user accounts
   *
   * Returns all cached user accounts that have signed in to this application.
   *
   * @returns Array of account information
   */
  getAllAccounts(): AccountInfo[] {
    if (!this._isInitialized) {
      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: Cannot get all accounts - MSAL not initialized');
      }
      return [];
    }
    return this.msalService.instance.getAllAccounts();
  }

  /**
   * Clear all authentication data and interaction state
   *
   * This method completely clears all authentication state, cached tokens,
   * and interaction state. Useful for troubleshooting authentication issues.
   *
   * @returns Promise that resolves when clearing is complete
   */
  async clearAllAuthenticationData(): Promise<void> {
    if (!this._isInitialized) {
      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: Cannot clear data - MSAL not initialized');
      }
      return;
    }

    try {
      // Clear active account
      this.msalService.instance.setActiveAccount(null);

      // Clear interaction state
      this.clearInteractionState();

      // Clear browser storage if possible
      try {
        const msalInstance = this.msalService.instance as unknown as MsalPublicClientApplication;
        if (msalInstance.browserStorage) {
          msalInstance.browserStorage.clear();
        }
      } catch (storageError) {
        if (isDebugMode()) {
          this.logger.warn('Microsoft Auth: Could not clear browser storage:', storageError);
        }
      }

      // Update local state
      this._isAuthenticated.set(false);
      this._userProfile.set(null);

      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: All authentication data cleared');
      }
    } catch (error) {
      this.logger.error('Microsoft Auth: Error clearing authentication data:', error);
      throw error;
    }
  }

  /**
   * Check if user is currently authenticated with Microsoft
   *
   * Synchronously checks whether there is an active Microsoft user account.
   *
   * @returns True if user is authenticated with Microsoft, false otherwise
   */
  get isAuthenticated(): boolean {
    return this._isAuthenticated();
  }

  /**
   * Purge the MSAL token cache (task 4.3, Defect 2)
   *
   * Calls msal-browser v5's `clearCache()` with no account filter, which
   * removes ALL cached accounts, their id_token/refresh_token credentials,
   * app metadata, temporary cache items, and the crypto keystore.
   *
   * The GTLC session is the httpOnly cookie — the MSAL cache is single-purpose
   * (acquire the ID token for the backend exchange) and must be spent once the
   * exchange succeeds, so a replayable token never lingers in web storage.
   *
   * MUST only be called after `handleRedirectObservable()` has resolved —
   * clearing mid-redirect would break the round-trip.
   */
  async purgeMsalCache(): Promise<void> {
    try {
      await this.msalService.instance.clearCache();
      this._isAuthenticated.set(false);
      this._userProfile.set(null);

      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: MSAL cache purged (id_token/refresh_token removed)');
      }
    } catch (error) {
      // A failed purge must never break the login that just succeeded — log
      // loudly and continue. The token is still bounded by sessionStorage.
      this.logger.error('Microsoft Auth: Failed to purge MSAL cache:', error);
    }
  }

  /**
   * Get user's display name
   *
   * Returns the display name of the current Microsoft user.
   *
   * @returns User's display name or email if name not available, or empty string
   */
  getUserDisplayName(): string {
    const account = this.getCurrentUser();
    return account?.name || account?.username || '';
  }

  /**
   * Get user's email address
   *
   * Returns the email address (username) of the current Microsoft user.
   *
   * @returns User's email address or empty string if not authenticated
   */
  getUserEmail(): string {
    const account = this.getCurrentUser();
    return account?.username || '';
  }

  /**
   * Get user's tenant ID
   *
   * Returns the Azure AD tenant ID of the current user.
   *
   * @returns User's tenant ID or empty string if not authenticated
   */
  getTenantId(): string {
    const account = this.getCurrentUser();
    return account?.tenantId || '';
  }

  /**
   * Check if user has specific scopes
   *
   * Verifies if the current user has been granted specific permissions/scopes.
   *
   * @param requiredScopes - Array of scopes to check
   * @returns True if user has all required scopes, false otherwise
   */
  async hasScopes(requiredScopes: string[]): Promise<boolean> {
    try {
      const result = await firstValueFrom(this.acquireTokenSilently({
        scopes: requiredScopes,
      }));

      return !!result?.accessToken;
    } catch (error) {
      if (isDebugMode()) {
        this.logger.debug('Microsoft Auth: Scope check failed:', error);
      }
      return false;
    }
  }

  /**
   * Clean up service resources and subscriptions
   *
   * Completes all observables and cleans up subscriptions to prevent memory leaks.
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

/**
 * Microsoft User interface (alias for AccountInfo)
 *
 * Represents a Microsoft user account with all available profile information.
 */
export type MicrosoftUser = AccountInfo;